import type { WarehouseRefModel } from '@supply-mover-2-0/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { workdStorage } from '../storage/workdStorage';

/**
 * RingCentral call preferences
 */
export interface RingCentralPreferences {
	callMethodPreference: string | null;
	deviceId: string | null;
	deviceName: string | null;
	forwardingNumberId: string | null;
	dialMethod: string | null;
	ringOutPhoneNumber: string | null;
}

/**
 * Vonage call preferences
 */
export interface VonagePreferences {
	callMethodPreference: 'vonage' | 'phone_app' | null;
}

/**
 * Reliable PBX call preferences
 */
export interface ReliablePbxPreferences {
	callMethodPreference: 'reliablepbx' | 'phone_app' | null;
}

/**
 * Email preferences
 */
export interface EmailPreferences {
	// How clicking an email address behaves: open the OS mail app (mailto) or
	// compose in-app. null = ask (show the chooser).
	sendMethodPreference: 'mailto' | 'compose' | null;
}

/**
 * User preferences state
 */
export interface PreferencesStoreState {
	language: string | null;
	theme: 'light' | 'dark' | null;
	ringCentral: RingCentralPreferences;
	vonage: VonagePreferences;
	reliablePbx: ReliablePbxPreferences;
	email: EmailPreferences;

	// Config-web preferences
	configSidebarCollapsed: boolean;
	configPublicUserId: string | null;

	// PD-13742: the current user's operational scope — the warehouse they are
	// "operating as". Hydrated from the server at bootstrap
	// (UserWarehouseQueryService.loadMyScopeIntoStore) and read by every
	// warehouse-scoped surface (the list warehouse chip, operation-form defaults,
	// the avatar switcher) as a single source of truth. This is a user-scope
	// slice, not warehouse-only: PD-13779 availability status joins here as a
	// sibling field, so the store scales by adding slices, not new stores.
	activeWarehouse: WarehouseRefModel | null;
	accessibleWarehouses: Array<WarehouseRefModel>;
}

/**
 * User preferences actions
 */
export interface PreferencesStoreActions {
	setLanguage: (language: string | null) => void;
	setTheme: (theme: 'light' | 'dark' | null) => void;
	setRingCentralPreference: <K extends keyof RingCentralPreferences>(
		key: K,
		value: RingCentralPreferences[K],
	) => void;
	setRingCentralPreferences: (prefs: Partial<RingCentralPreferences>) => void;
	setVonagePreference: <K extends keyof VonagePreferences>(
		key: K,
		value: VonagePreferences[K],
	) => void;
	setVonagePreferences: (prefs: Partial<VonagePreferences>) => void;
	setReliablePbxPreference: <K extends keyof ReliablePbxPreferences>(
		key: K,
		value: ReliablePbxPreferences[K],
	) => void;
	setReliablePbxPreferences: (prefs: Partial<ReliablePbxPreferences>) => void;
	setEmailPreference: <K extends keyof EmailPreferences>(
		key: K,
		value: EmailPreferences[K],
	) => void;
	setConfigSidebarCollapsed: (collapsed: boolean) => void;
	setConfigPublicUserId: (userId: string | null) => void;
	// PD-13742: warehouse-scope setters. `setActiveWarehouse` is the live switch
	// (avatar menu / profile save); `setWarehouseScope` is the bootstrap hydrate.
	setActiveWarehouse: (warehouse: WarehouseRefModel | null) => void;
	setWarehouseScope: (scope: {
		activeWarehouse: WarehouseRefModel | null;
		accessibleWarehouses: Array<WarehouseRefModel>;
	}) => void;
	clearAll: () => void;
}

const initialRingCentralState: RingCentralPreferences = {
	callMethodPreference: null,
	deviceId: null,
	deviceName: null,
	forwardingNumberId: null,
	dialMethod: null,
	ringOutPhoneNumber: null,
};

const initialVonageState: VonagePreferences = {
	callMethodPreference: null,
};

const initialReliablePbxState: ReliablePbxPreferences = {
	callMethodPreference: null,
};

const initialEmailState: EmailPreferences = {
	sendMethodPreference: null,
};

const initialState: PreferencesStoreState = {
	language: null,
	theme: null,
	ringCentral: initialRingCentralState,
	vonage: initialVonageState,
	reliablePbx: initialReliablePbxState,
	email: initialEmailState,
	configSidebarCollapsed: false,
	configPublicUserId: null,
	activeWarehouse: null,
	accessibleWarehouses: [],
};

/**
 * Zustand store for user preferences.
 * Persists to localStorage with workd:preferences:state key.
 *
 * Stores:
 * - Language preference
 * - Theme preference (light/dark)
 * - RingCentral call settings
 * - Vonage call settings
 * - Config-web sidebar and user settings
 */
export const usePreferencesStore = create<
	PreferencesStoreState & PreferencesStoreActions
>()(
	persist(
		(set) => ({
			...initialState,

			setLanguage: (language) => {
				set({ language });
			},

			setTheme: (theme) => {
				set({ theme });
			},

			setRingCentralPreference: (key, value) => {
				set((state) => ({
					ringCentral: {
						...state.ringCentral,
						[key]: value,
					},
				}));
			},

			setRingCentralPreferences: (prefs) => {
				set((state) => ({
					ringCentral: {
						...state.ringCentral,
						...prefs,
					},
				}));
			},

			setVonagePreference: (key, value) => {
				set((state) => ({
					vonage: {
						...state.vonage,
						[key]: value,
					},
				}));
			},

			setVonagePreferences: (prefs) => {
				set((state) => ({
					vonage: {
						...state.vonage,
						...prefs,
					},
				}));
			},

			setReliablePbxPreference: (key, value) => {
				set((state) => ({
					reliablePbx: {
						...state.reliablePbx,
						[key]: value,
					},
				}));
			},

			setReliablePbxPreferences: (prefs) => {
				set((state) => ({
					reliablePbx: {
						...state.reliablePbx,
						...prefs,
					},
				}));
			},

			setEmailPreference: (key, value) => {
				set((state) => ({
					email: {
						...state.email,
						[key]: value,
					},
				}));
			},

			setConfigSidebarCollapsed: (configSidebarCollapsed) => {
				set({ configSidebarCollapsed });
			},

			setConfigPublicUserId: (configPublicUserId) => {
				set({ configPublicUserId });
			},

			setActiveWarehouse: (activeWarehouse) => {
				set({ activeWarehouse });
			},

			setWarehouseScope: ({ activeWarehouse, accessibleWarehouses }) => {
				set({ activeWarehouse, accessibleWarehouses });
			},

			clearAll: () => {
				set(initialState);
			},
		}),
		{
			name: 'workd:preferences:state',
			storage: workdStorage,
		},
	),
);
